import { useQuery } from "@tanstack/react-query";
import { reviewerApi, apiBaseConfigured, type CaseSummary } from "@/lib/api";

export interface UseCasesOptions {
  /** When true, include closed cases (declined-by-applicant, expired). Default: false. */
  includeClosed?: boolean;
}

export interface UseCasesResult {
  cases: CaseSummary[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useCases(options?: UseCasesOptions): UseCasesResult {
  const enabled = apiBaseConfigured();
  const includeClosed = options?.includeClosed ?? false;

  const query = useQuery({
    // includeClosed in the query key so the cache distinguishes the two views.
    queryKey: ["reviewer", "sessions", { includeClosed }],
    queryFn: () => reviewerApi.listSessions({ includeClosed }),
    enabled,
    retry: 1,
    staleTime: 30_000,
    // Keep previous data visible across remounts so navigating back to the
    // dashboard doesn't flash the skeleton.
    placeholderData: (prev) => prev,
  });

  // Vouchers create their own verification_sessions rows; the operator queue
  // should only show applicant sessions.
  const filtered = (query.data ?? []).filter((c) => !c.is_voucher);

  return {
    cases: filtered,
    loading: enabled && query.isLoading,
    error: (query.error as Error) ?? null,
    refetch: () => query.refetch(),
  };
}
